import { z } from 'zod'
import { orderResponseSchema } from './order-response.schema'

// server-side query for the account Orders tab: page-number paging + optional filters + sort.
// the filter values are friendly string keys (not the raw T-Bank numeric enums) so the wire
// contract stays readable; the backend maps each key to its OrderDirection / OrderType /
// OrderExecutionReportStatus code.
export const orderDirectionFilterValues = ['buy', 'sell'] as const
export const orderTypeFilterValues = ['limit', 'market'] as const
export const orderStatusFilterValues = [
  'new',
  'partiallyfill',
  'fill',
  'rejected',
  'cancelled',
] as const

// sortable columns. price is the per-share order price; sum is the order value (per-share × lots).
export const orderSortFields = [
  'date',
  'instrument',
  'direction',
  'price',
  'sum',
  'status',
] as const
export const orderSortDirections = ['asc', 'desc'] as const

// page-size choices offered by the page-size selector; the first is the default.
export const ORDERS_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const
export const DEFAULT_ORDERS_PAGE_SIZE = 20

export type OrderDirectionFilter = (typeof orderDirectionFilterValues)[number]
export type OrderTypeFilter = (typeof orderTypeFilterValues)[number]
export type OrderStatusFilter = (typeof orderStatusFilterValues)[number]
export type OrderSortField = (typeof orderSortFields)[number]
export type OrderSortDirection = (typeof orderSortDirections)[number]

// instrument-type groups the Orders tab's instrument filter offers ("All stocks / bonds / ETFs").
// the values are T-Bank's own lowercase instrumentType strings, as cached in TbankInstrument.
export const orderInstrumentTypeFilterValues = ['share', 'bond', 'etf'] as const
export type OrderInstrumentTypeFilter = (typeof orderInstrumentTypeFilterValues)[number]

// the numeric T-Bank codes each friendly filter key stands for (OrderDirection, OrderType,
// OrderExecutionReportStatus). shared so the backend's SQL filter and the frontend's client-side
// chart filter resolve a key to the same code; the backend spec pins them to the SDK enums.
export const ORDER_DIRECTION_FILTER_CODE: Record<OrderDirectionFilter, number> = {
  buy: 1,
  sell: 2,
}
export const ORDER_TYPE_FILTER_CODE: Record<OrderTypeFilter, number> = {
  limit: 1,
  market: 2,
}
export const ORDER_STATUS_FILTER_CODE: Record<OrderStatusFilter, number> = {
  fill: 1,
  rejected: 2,
  cancelled: 3,
  new: 4,
  partiallyfill: 5,
}

const orderDateBoundSchema = z.union([
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD format'),
  z.string().datetime({ offset: true }),
])

export const ordersListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(DEFAULT_ORDERS_PAGE_SIZE),
  status: z.enum(orderStatusFilterValues).optional(),
  direction: z.enum(orderDirectionFilterValues).optional(),
  orderType: z.enum(orderTypeFilterValues).optional(),
  // filter to a single instrument by its uid (the Orders tab offers a dropdown of the instruments
  // that actually have orders on the account)
  instrumentUid: z.string().min(1).optional(),
  // filter to every instrument of one type (per the cached TbankInstrument metadata); an order
  // whose instrument has no cached metadata matches no type
  instrumentType: z.enum(orderInstrumentTypeFilterValues).optional(),
  // inclusive range on the order's creation date. either an exact ISO-8601 instant (what the app
  // sends — the user's local day bounds, so the server window matches the client-side chart) or a
  // bare YYYY-MM-DD, interpreted as UTC day bounds
  dateFrom: orderDateBoundSchema.optional(),
  dateTo: orderDateBoundSchema.optional(),
  sortBy: z.enum(orderSortFields).default('date'),
  sortDir: z.enum(orderSortDirections).default('desc'),
})

export type OrdersListQueryDto = z.infer<typeof ordersListQuerySchema>

export const ordersListResponseSchema = z.object({
  entries: z.array(orderResponseSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
})

export type OrdersListResponseDto = z.infer<typeof ordersListResponseSchema>

// one distinct instrument that has at least one confirmed order on the account, backing the Orders
// tab's instrument filter dropdown. ticker may be null for a row that never resolved one; name and
// instrumentType come from the cached instrument metadata and are null when none is cached.
export const orderInstrumentSchema = z.object({
  instrumentUid: z.string(),
  ticker: z.string().nullable(),
  name: z.string().nullable(),
  instrumentType: z.string().nullable(),
})

export type OrderInstrumentDto = z.infer<typeof orderInstrumentSchema>

export const orderInstrumentsResponseSchema = z.array(orderInstrumentSchema)

export type OrderInstrumentsResponseDto = z.infer<typeof orderInstrumentsResponseSchema>
